import { TFunction } from 'i18next'
import { z } from 'zod'

import { Manager } from '@/api/managers'
import { EmailLink } from '@/components/ui/email-link'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { SortOrderSchema, TableColumn } from '@/components/ui/table'
import { Typography } from '@/components/ui/typography'
import { formatDateOnly } from '@/utils/date'

import { ActiveStatusBadge } from '../shared/active-status-badge'
import { PersonNameCell } from '../shared/person-name-cell'
import { ManagerSortKey, ManagerSortKeySchema } from './managers.sort-keys'

export const MANAGERS_TABLE_ID = 'managers-table'

export const managersTableFiltersSchema = z.object({
  fromDate: z.coerce.number().optional(),
  limit: z.coerce.number().optional(),
  search: z.string().optional(),
  skip: z.coerce.number().optional(),
  sortBy: ManagerSortKeySchema.optional(),
  sortOrder: SortOrderSchema.optional(),
  toDate: z.coerce.number().optional(),
})

export type ManagersTableFilters = z.infer<typeof managersTableFiltersSchema>

export const managersTableInitialFilters: ManagersTableFilters = {
  fromDate: undefined,
  limit: DEFAULT_LIMIT,
  search: '',
  skip: 0,
  sortBy: ManagerSortKey.createdAt,
  sortOrder: 'desc',
  toDate: undefined,
}

export const getManagersTableColumns = (t: TFunction<['organisation']>): TableColumn<Manager>[] => [
  {
    id: ManagerSortKey.name,
    render: (manager) => <PersonNameCell user={manager.user} />,
    sortKey: ManagerSortKey.name,
    title: t('organisation:managers.list.columns.name'),
    width: '26rem',
  },
  {
    id: ManagerSortKey.email,
    render: (manager) => (
      <Typography color="black500" size="text-small" weight="medium" breakLines={1}>
        <EmailLink email={manager.user.email} />
      </Typography>
    ),
    sortKey: ManagerSortKey.email,
    title: t('organisation:managers.list.columns.email'),
    width: '28rem',
  },
  {
    id: ManagerSortKey.status,
    render: (manager) => <ActiveStatusBadge isActive={manager.user.isActive} />,
    sortKey: ManagerSortKey.status,
    title: t('organisation:managers.list.columns.status'),
    width: '14rem',
  },
  {
    id: ManagerSortKey.createdAt,
    render: (manager) => (
      <Typography color="black500" size="text-small" weight="medium">
        {formatDateOnly(new Date(manager.createdAt))}
      </Typography>
    ),
    sortKey: ManagerSortKey.createdAt,
    title: t('organisation:managers.list.columns.createdAt'),
    width: '14rem',
  },
]
